import React, { useCallback, useContext, useRef, useState } from 'react';
import { compose } from 'recompose';
import { Context, ContextProvider } from './store';
import { css } from '@emotion/react';
import { Body1, Header1, PageTitle } from '../blocks';
import { colors } from '@/styles';
import ZipList from '../ZipList';
import SubregionList from '../SubregionList';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { OnwardSwitch } from '@/styles/blocks';
import SubregionMap from '../SubregionMap';
import Snackbar from '@/components/Snackbar';

const CarrierOptions = () => {
    const mapRef = useRef(null);
    const [root, setRoot] = useState(null);
    const { state, loading, callbacks } = useContext(Context);
    const [highlightOverride, setHighlightOverride] = useState(false);

    const initRoot = useCallback((node) => {
        if (node !== null) {
            setRoot(node);
        }
    }, []);

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                background: ${colors.white.primary};
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            {/* Header */}
            <Grid
                container
                css={css`
                    padding: 23px 45px;
                    border-bottom: 1px solid ${colors.greys[0]};
                    align-items: center;
                    justify-content: space-between;
                `}
            >
                <Grid item>
                    <PageTitle>Carrier Regions</PageTitle>
                </Grid>
                <Grid
                    container
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                        justify-content: flex-end;
                    `}
                >
                    <OnwardSwitch
                        label={'Test Accounts'}
                        checked={state.testFlag}
                        onChange={() => callbacks.setTestFlag((prev) => !prev)}
                        css={css`
                            margin-right: 20px;
                        `}
                    />
                    <OnwardSwitch
                        label={'Highlight All'}
                        checked={highlightOverride}
                        onChange={() => setHighlightOverride((prev) => !prev)}
                    />
                </Grid>
            </Grid>

            {/* Content */}
            <Grid
                container
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    min-height: 0;
                    flex-wrap: nowrap;
                `}
            >
                {/* Subregions */}
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        min-width: 320px;
                        flex-wrap: nowrap;
                        /* Scroll instead of stretching the row (and the map). */
                        min-height: 0;
                        overflow-y: auto;
                        border-right: 1px solid ${colors.greys[0]};
                    `}
                >
                    <SubregionList
                        // Read-only: partnered carriers' subregions (Hasura doesn't block writes yet, epic #12).
                        options={{
                            enableAddSubregions: false,
                            enableImport: false,
                            enableExport: true,
                            readOnly: true,
                        }}
                        loading={loading}
                        filter={state.filter}
                        selected={state.selectedSubregion}
                        subregions={state.subregions}
                        callbacks={callbacks}
                    />
                </Grid>

                {/* Zips */}
                {state.selectedSubregion ? (
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            min-width: 320px;
                            flex-wrap: nowrap;
                            border-right: 1px solid ${colors.greys[0]};
                        `}
                    >
                        <Grid
                            item
                            css={css`
                                padding: 18px 20px 0px 20px;
                            `}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Receiving Warehouse"
                                value={state.selectedSubregion.warehouse_id || 'DEFAULT'}
                                disabled
                            >
                                <MenuItem key="DEFAULT" value={'DEFAULT'}>
                                    Default (Preferred Inbound)
                                </MenuItem>
                                {(state.locationsByClient[state.selectedSubregion.client_id] || []).map(
                                    (location, i) => (
                                        <MenuItem key={location.location_id} value={location.location_id}>
                                            {location.location_name || `Warehouse ${i + 1}`}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        <ZipList
                            options={{ readOnly: true }}
                            loading={loading}
                            filter={state.filter}
                            subregion={state.selectedSubregion}
                            zips={state.selectedSubregion.zips}
                            callbacks={callbacks}
                        />
                    </Grid>
                ) : null}

                {/* Map */}
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                        overflow: hidden;
                        position: relative;
                    `}
                >
                    <div
                        ref={initRoot}
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                        `}
                    />
                    <SubregionMap
                        ref={mapRef}
                        node={root}
                        subregions={state.subregions}
                        callbacks={{
                            ...callbacks,
                            onLoad: (map) => {},
                        }}
                        highlightOverride={highlightOverride}
                        selectedSubregion={state.selectedSubregion}
                        hoveredSubregion={state.hoveredSubregion}
                        selectedZips={state.selectedZips}
                        hoveredSubregionZip={state.hoveredSubregionZip}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(CarrierOptions);
